/*
 * This component is an input component for continuity across the app. Error handling included.
 *
 * @author Cargomatic Engineering
 * @usage <CargomaticInput />
 *
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import LocationAutosuggest from 'components/site/location-autosuggest';
import PhoneField from 'components/v2/forms/PhoneField';
import Label from './forms/Label';
import ErrorLabel from './forms/ErrorLabel';
import TextField from './forms/TextField';
import TextArea from './forms/TextArea';
import Checkbox from './forms/Checkbox';
import { getClassName } from '../../utils/ui/classes';
import EyeIcon from './icons/EyeIcon';
import EyeOffIcon from './icons/EyeOffIcon';
import SearchField from './SearchField';
const CargomaticInput = (props) => {
    const { id, checked, disabled, onChange, onBlur, onSelect, label, error, hideError, info, maxLength, type, value, autoComplete, hideLabel, placeholder, className, inputClassNames, inputStyles, autoFocus, onKeyDown, name, min, max, step, rows, showPasswordIcon, searchType, readOnly, dataTestId = 'dataTestId-Cargomatic-Input-v2', } = props;
    const [showPassword, setShowPassword] = useState(false);
    const containerClass = getClassName({
        error: error && error.message,
        relative: true,
    }, className);
    const inputProps = {
        id,
        onChange,
        onBlur,
        onKeyDown,
        checked,
        value,
        maxLength,
        autoComplete,
        autoFocus,
        disabled,
        placeholder,
        name,
        min,
        max,
        step,
    };
    function toggleShowPasswordIcon() {
        setShowPassword(!showPassword);
    }
    function renderShowPasswordIcon() {
        let icon;
        if (type !== 'password' || !showPasswordIcon) {
            return null;
        }
        if (showPassword) {
            icon = React.createElement(EyeIcon, null);
        }
        else {
            icon = React.createElement(EyeOffIcon, null);
        }
        return (React.createElement("div", { "data-testid": "div-1450db36", className: "absolute top-0 right-0" },
            React.createElement("button", { "data-testid": "button-83386043", type: "button", className: "flex items-center justify-center px-2 py-2 text-gray-400 appearance-none", onClick: toggleShowPasswordIcon }, icon)));
    }
    function renderLabel() {
        if (hideLabel) {
            return null;
        }
        const className = { 'mb-1': info, 'mb-2': !info };
        return (React.createElement(Label, { dataTestId: "Label-21f84b69", helptext: info, className: className }, label));
    }
    function renderErrorMessage(id = '') {
        if (!(error && error.message) || hideError) {
            return null;
        }
        return (React.createElement(ErrorLabel, { dataTestId: "ErrorLabel-046eef0e", id: id, className: "mt-1" }, error.message));
    }
    function handlePhoneChange(phone) {
        onChange(phone);
    }
    if (type === 'checkbox') {
        return (React.createElement(Checkbox, { dataTestId: "Checkbox-1cee22e3", id: id, "data-testId": id, className: containerClass, inputClassNames: inputClassNames, style: inputStyles, hideLabel: hideLabel, name: name, onChange: onChange, checked: checked || value, disabled: disabled, placeholder: placeholder, info: info, error: error }, label));
    }
    if (type === 'phone') {
        return (React.createElement("div", { "data-testid": "div-5892bc39", className: containerClass },
            renderLabel(),
            React.createElement("div", { "data-testid": "div-04232bc4", style: inputStyles },
                React.createElement(PhoneField, { dataTestId: "PhoneField-ba8f8702", className: inputClassNames, value: value, onChange: handlePhoneChange })),
            renderErrorMessage()));
    }
    if (type === 'location') {
        return (React.createElement("div", { "data-testid": "div-841a3ed2", className: containerClass },
            renderLabel(),
            React.createElement("div", { "data-testid": "div-c0c13462", style: inputStyles },
                React.createElement(LocationAutosuggest, { address: value, value: value, searchType: searchType, suggestions: [{ address: value }], callbackSelectedItem: (item) => onSelect(item), onChange: onChange, readOnly: readOnly })),
            renderErrorMessage()));
    }
    if (type === 'textarea') {
        return (React.createElement("div", { "data-testid": "div-4c0832cb", className: containerClass },
            renderLabel(),
            React.createElement(TextArea, Object.assign({ dataTestId: "TextArea-cc512eee", className: [inputClassNames] }, inputProps, { rows: rows, readonly: readOnly, style: inputStyles })),
            renderErrorMessage(name)));
    }
    if (type === 'search') {
        return (React.createElement(SearchField, { dataTestId: "SearchField-fc71e9db", onChange: onChange }));
    }
    let actualType;
    if (type !== 'password') {
        actualType = type;
    }
    else {
        actualType = showPassword ? 'text' : 'password';
    }
    return ((React.createElement("div", { "data-testid": dataTestId, className: containerClass },
        renderLabel(),
        React.createElement("div", { "data-testid": "div-768e09bd", className: "relative" },
            React.createElement(TextField, Object.assign({ dataTestId: "TextField-1fcfe679", "data-testid": "TextField-81d9c91b", className: [inputClassNames], type: actualType }, inputProps, { readOnly: readOnly, style: inputStyles, name: name })),
            !disabled && renderShowPasswordIcon()),
        renderErrorMessage())));
};
CargomaticInput.propTypes = {
    id: PropTypes.string,
    name: PropTypes.string,
    onChange: PropTypes.func.isRequired, // onChange callback
    onBlur: PropTypes.func,
    onKeyDown: PropTypes.func,
    label: PropTypes.string, // input label
    autoComplete: PropTypes.string, // input autocomplete
    autoFocus: PropTypes.bool,
    error: PropTypes.object, // error object to display {message: ''}
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool,
        PropTypes.number,
    ]),
    type: PropTypes.string,
    checked: PropTypes.bool,
    info: PropTypes.string,
    hideError: PropTypes.bool,
    hideLabel: PropTypes.bool,
    disabled: PropTypes.bool,
    className: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
        PropTypes.array,
    ]),
    inputClassNames: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
        PropTypes.array,
    ]),
    inputStyles: PropTypes.object, // React CSS Style Object
    placeholder: PropTypes.string,
    maxLength: PropTypes.number,
    min: PropTypes.number,
    max: PropTypes.number,
    step: PropTypes.number,
    rows: PropTypes.number,
    showPasswordIcon: PropTypes.bool,
    searchType: PropTypes.object,
    readOnly: PropTypes.bool,
};
CargomaticInput.defaultProps = {
    autoComplete: '',
    autoFocus: false,
    label: '',
    hideLabel: false,
    hideError: false,
    error: {},
    type: 'text',
    disabled: false,
    checked: false,
    inputClassNames: {},
    inputStyles: {},
    showPasswordIcon: true,
    onBlur: () => { },
};
export default CargomaticInput;
